import { useEffect } from 'react'
import { startSilentAudio, stopSilentAudio } from '@/lib/silentAudio'

type MediaSessionLike = {
  metadata: unknown
  playbackState?: 'none' | 'paused' | 'playing'
  setActionHandler: (
    action: 'stop' | 'pause' | 'play',
    handler: (() => void) | null,
  ) => void
}

function getMediaSession(): MediaSessionLike | null {
  if (typeof navigator === 'undefined') return null
  const ms = (navigator as Navigator & { mediaSession?: MediaSessionLike }).mediaSession
  return ms ?? null
}

/**
 * While `active`, plays inaudible audio and registers Media Session metadata
 * so the browser treats the tab as actively using audio. Gives mobile users
 * (especially Android Chrome) longer background tolerance during recording.
 * No-op where APIs are missing.
 */
export function useRecordingKeepalive(active: boolean, onStop?: () => void): void {
  useEffect(() => {
    if (!active) return

    let cancelled = false
    const session = getMediaSession()
    const MediaMetadataCtor = (window as Window & {
      MediaMetadata?: new (init: Record<string, unknown>) => unknown
    }).MediaMetadata

    void startSilentAudio().catch(() => undefined)

    if (session) {
      try {
        if (MediaMetadataCtor) {
          session.metadata = new MediaMetadataCtor({
            title: 'Recording in progress',
            artist: 'Medical Congress Solution Platform',
          })
        }
        session.playbackState = 'playing'
        if (onStop) {
          session.setActionHandler('stop', () => onStop())
        }
      } catch (err) {
        console.warn('[recordingKeepalive] mediaSession setup failed', err)
      }
    }

    return () => {
      cancelled = true
      void stopSilentAudio()
      if (session) {
        try {
          session.playbackState = 'none'
          session.metadata = null
          session.setActionHandler('stop', null)
        } catch (err) {
          console.warn('[recordingKeepalive] mediaSession teardown failed', err)
        }
      }
      void cancelled
    }
  }, [active, onStop])
}
